"use client";

import Image from "next/image";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { KeyboardEvent, MouseEvent, useCallback } from "react";
import { getTitleLines } from "~/lib/titleLines";
import { TabType } from "./types";

export interface ITabsProps {
	readonly tabs: ReadonlyArray<ITab>;
	readonly tabsKey: string;
	readonly defaultSelectedTabKey: string;
	readonly tabType: TabType;
}

export interface ITab {
	readonly tabKey: string;
	readonly image?: React.FC<React.SVGProps<SVGSVGElement>>;
	readonly imagePath?: string;
	readonly headingCaption: string;
	readonly subheadingCaption?: string;
}

const Tabs: React.FC<ITabsProps> = ({ tabs, tabsKey, defaultSelectedTabKey, tabType }) => {
	const router = useRouter();
	const searchParams = useSearchParams();
	const selectedTabKey = searchParams?.get(tabsKey) || defaultSelectedTabKey;
	const pathname = usePathname();

	// Update the url when the selected tab key changes
	const onTabClick = useCallback(
		(tabKey: string) => {
			const params = new URLSearchParams(Array.from(searchParams.entries()));
			params.set(tabsKey, tabKey);

			router.replace(`${pathname}?${params.toString()}`, { scroll: false });
		},
		[searchParams, tabsKey, router, pathname],
	);

	// Return the content
	return (
		<nav>
			<menu>
				<ul className="flex flex-col justify-between gap-8 md:flex-row">
					{tabs.map(tab => {
						const selected = tab.tabKey === selectedTabKey;
						return (
							<Tab
								key={`big_tab_${tab.tabKey}`}
								id={`big_tab_${tab.tabKey}`}
								tab={tab}
								onTabClick={onTabClick}
								selected={selected}
								tabType={tabType}
							/>
						);
					})}
				</ul>
			</menu>
		</nav>
	);
};
export default Tabs;

export interface ITabsNoRouterProps {
	readonly tabs: ReadonlyArray<ITab>;
	readonly selectedTabKey: string;
	readonly setSelectedTabKey: { (tabKey: string): void };
	readonly tabType: TabType;
}

export const TabsNoRouter: React.FC<ITabsNoRouterProps> = ({
	tabs,
	selectedTabKey,
	setSelectedTabKey,
	tabType,
}) => {
	const onTabClick = useCallback(
		(tabKey: string) => {
			setSelectedTabKey(tabKey);
		},
		[setSelectedTabKey],
	);
	const selectedTab = tabs.find((tab: ITab) => {
		return tab.tabKey === selectedTabKey;
	});
	const selectedTabCaption = selectedTab ? selectedTab.headingCaption : "";

	// Return the content
	return (
		<nav>
			<menu>
				<ul className="flex flex-row items-stretch justify-between mx-auto sm:flex-col max-w-none sm:max-w-sm gap-x-1 sm:gap-x-8 gap-y-2 lg:max-w-none lg:flex-row">
					{tabs.map(tab => {
						const selected = tab.tabKey === selectedTabKey;
						return (
							<Tab
								key={`big_tab_${tab.tabKey}`}
								id={`big_tab_${tab.tabKey}`}
								tab={tab}
								onTabClick={onTabClick}
								selected={selected}
								tabType={tabType}
							/>
						);
					})}
				</ul>
				<div className="text-lg font-bold sm:hidden font-title">{selectedTabCaption}</div>
			</menu>
		</nav>
	);
};

//https://github.com/vercel/next.js/issues/36447
//https://www.reddit.com/r/nextjs/comments/xgpdwm/scroll_in_next_js/
//https://www.slingacademy.com/article/next-js-link-disable-auto-scrolling-to-top-on-page-changes/

interface ITabProps {
	readonly tab: ITab;
	readonly onTabClick: (tabKey: string) => void;
	readonly selected: boolean;
	readonly tabType: TabType;
	readonly id: string;
}
const Tab: React.FC<ITabProps> = ({ tab, onTabClick, selected, tabType, id }) => {
	const onButtonClick = useCallback(
		(event: MouseEvent) => {
			onTabClick(tab.tabKey);
			event.preventDefault();
		},
		[onTabClick, tab],
	);
	const onButtonKeyboard = useCallback(
		(event: KeyboardEvent) => {
			if (event.key === "Enter" || event.key === " 	") {
				onTabClick(tab.tabKey);
			}
		},
		[onTabClick, tab],
	);
	let tabElement: JSX.Element;
	const titleLines = getTitleLines(tab.headingCaption);
	switch (tabType) {
		case TabType.big:
			tabElement = (
				<li className={`w-full md:w-auto`} id={id}>
					<button
						className={`group flex flex-col sm:flex-row md:flex-col items-center md:w-auto w-full text-center sm:text-left md:text-center`}
						tabIndex={0}
						onMouseDown={onButtonClick}
						onKeyDown={onButtonKeyboard}
					>
						<div className="relative inline-block w-20 h-20 overflow-hidden">
							{tab.imagePath ? (
								<Image
									alt=""
									src={tab.imagePath}
									fill
									className="relative object-contain"
									sizes="80px"
								/>
							) : null}
						</div>
						<div className="flex flex-col items-stretch pt-1 sm:ml-2 sm:pl-2 md:items-stretch md:pl-0">
							<div
								className={`text-epic-light-red font-lg font-title group-hover:[text-shadow:_0_0_1px_rgb(198_39_21_/_var(--tw-text-opacity))] group-focus-within:[text-shadow:_0_0_1px_rgb(198_39_21_/_var(--tw-text-opacity))] ${
									selected ? "[text-shadow:_0_0_1px_rgb(198_39_21_/_var(--tw-text-opacity))]" : ""
								}`}
							>
								{titleLines
									? titleLines.map(titleLine => (
											<div
												className="sm:inline"
												key={`title_line_${titleLine.replaceAll(" ", "_")}`}
												id={`title_line_${titleLine.replaceAll(" ", "_")}`}
											>
												{titleLine}
											</div>
									  ))
									: null}
							</div>
							<div className={`font-title font-semibold`}>{tab.subheadingCaption}</div>
							<div>
								<div
									className={`${
										selected
											? "w-full border-b-2 pb-1 border-epic-red ease-in-out duration-300 transition-[width]"
											: "w-0"
									}`}
								></div>
							</div>
						</div>
					</button>
				</li>
			);
			break;

		default:
			tabElement = (
				<li className={`w-full md:w-auto md:basis-1 md:grow md:flex md:items-stretch`} id={id}>
					<button
						onMouseDown={onButtonClick}
						onKeyDown={onButtonKeyboard}
						className={`${
							selected ? "bg-epic-red text-white fill-white" : "bg-white"
						} group w-full flex items-center justify-center font-title font-semibold text-black fill-black hover:text-white focus-within:text-white  hover:bg-epic-red hover:fill-white focus-within:fill-white focus-within:bg-epic-red transition-all duration-300 ease-in-out px-1 py-1 sm:px-4 sm:py-2 border border-epic-red`}
					>
						{tab.image ? (
							<tab.image
								color={selected ? "white" : "#CD1543"}
								className={`sm:mr-2 transition-all duration-300 ease-in-out h-5 sm:h-8 aspect-square ${
									selected
										? "text-white"
										: "text-epic-red group-hover:text-white group-focus-within:text-white"
								}`}
							/>
						) : null}
						<span className="hidden sm:block">{tab.headingCaption}</span>
					</button>
				</li>
			);
			break;
	}
	return tabElement;
};
